import type { AdminEvent } from '@mds/shared/types/event';

import FormBuilder, { LockedRow } from '@/components/events/form-builder';
import Card from '@/components/ui/card';
import Chip from '@/components/ui/chip';
import { questionSetDraftOf, questionSetInputOf } from '@/domain/form-builder';
import { REGISTRATION_FORM_CHANGED } from '@/domain/registrations';
import * as registrationForm from '@/events/registration-form.api';
import { stamp } from '@mds/shared/lib/dates';

/** The fields every registration asks, whatever the form's own questions are. */
export function RegistrationAlwaysAsked({ priced }: { priced: boolean }) {
  return (
    <>
      <LockedRow label="Full name" detail="Required" />
      <LockedRow
        label="Email"
        detail={
          priced ? 'Required · the ticket is emailed' : 'Required online · optional at the desk'
        }
      />
      <LockedRow label="Phone" detail="Required · to reach a waitlisted or unpaid party" />
      <LockedRow label="Seats" detail="Required" />
      <LockedRow
        label="Keep me posted about events at MDS"
        detail="Optional · online only · turns on email consent"
      />
    </>
  );
}

/**
 * What an event asks when someone registers — on the public registration page,
 * and when staff add a party from the attendee list. Required questions bind
 * both.
 *
 * Unlike the feedback form it never locks: each answer keeps a copy of the
 * question it answered, so an edit cannot rewrite what anyone already said.
 */
export default function RegistrationFormBuilder({
  event,
  actionsSlot,
}: {
  event: AdminEvent;
  /** The dialog footer that Save and Discard are drawn into. */
  actionsSlot: HTMLElement | null;
}) {
  const priced = event.price.amountMinor > 0;
  return (
    <FormBuilder
      queryKey={['registration-form', event.id]}
      load={() => registrationForm.getForm(event.id)}
      save={(draft, version) =>
        registrationForm.replaceForm(event.id, {
          expectedVersion: version,
          ...questionSetInputOf(draft),
        })
      }
      draftOf={questionSetDraftOf}
      actionsSlot={actionsSlot}
      changedCode={REGISTRATION_FORM_CHANGED}
      noun="registration form"
      savedNotice={() => 'Saved. Registrations from now on answer these questions.'}
      questionsHint="Optional. Asked after the details above. Group related questions under a section heading and they appear together."
      intro={({ loaded, mayManage }) => (
        <Card
          title="Registration form"
          action={loaded.updatedAt ? <Chip>Saved {stamp(loaded.updatedAt)}</Chip> : undefined}
        >
          <div className="flex flex-col gap-4 text-base">
            <p className="m-0 text-ink-secondary">
              Asked on the public registration page, and when you add a party from the attendee
              list. Required questions have to be answered either way.
            </p>
            <p className="m-0 text-ink-muted">
              It can change at any time. Answers already given keep the question as it was asked,
              and anyone halfway through the old form is asked to check their answers again.
            </p>
            {!mayManage && (
              <p className="m-0 text-ink-muted">You can read this form but not change it.</p>
            )}
          </div>
        </Card>
      )}
      alwaysAsked={() => <RegistrationAlwaysAsked priced={priced} />}
    />
  );
}
